import React, { useState, useEffect, useRef, useMemo } from 'react';
import { apiCall } from '../services/api';
import toast from 'react-hot-toast';
import VirtualizedList from './VirtualizedList';
import { useCache } from '../hooks/useCache';
import { useNotifications } from '../contexts/NotificationContext';

interface FileBrowserProps {
  bucketName: string;
  s3Client: any;
  permissions: {
    list: boolean;
    download: boolean;
    upload: boolean;
    delete: boolean;
    maxObjectSize: number;
    monthlyDataTransferCap: number;
  };
  refreshTrigger?: number;
  onPrefixChange?: (prefix: string) => void;
  initialFolderPath?: string; // For folder-level permission restrictions
}

interface S3Object {
  key: string;
  size: number;
  lastModified: string;
  isImage: boolean;
  isVideo: boolean;
  isFolder: boolean;
  name: string;
}

interface BucketAnalytics {
  bucketName: string;
  totalSize: number;
  fileCount: number;
  lastModified: string | null;
}

interface BreadcrumbItem {
  name: string;
  path: string;
}

const FileBrowser: React.FC<FileBrowserProps> = ({ bucketName, permissions, refreshTrigger, onPrefixChange, initialFolderPath }) => {
  const [objects, setObjects] = useState<S3Object[]>([]);
  const [folders, setFolders] = useState<S3Object[]>([]);
  const [analytics, setAnalytics] = useState<BucketAnalytics | null>(null);
  const [loading, setLoading] = useState(false);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<{
    key: keyof S3Object;
    direction: 'asc' | 'desc';
  } | null>(null);

  const [prefix, setPrefix] = useState(initialFolderPath || '');
  const [restrictedToFolder, setRestrictedToFolder] = useState<string | null>(initialFolderPath || null);
  const [previewModal, setPreviewModal] = useState<{ url: string; type: 'image' | 'video'; name: string } | null>(null);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ key: string; fileName: string } | null>(null);
  const refreshTriggerRef = useRef(refreshTrigger);
  
  // Track the initial folder path to detect changes
  const initialFolderPathRef = useRef(initialFolderPath);
  
  // Multi-select state
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [isDownloadingBatch, setIsDownloadingBatch] = useState(false);

  // Compute the effective prefix - use initialFolderPath when it changes, otherwise use current prefix
  const effectivePrefix = initialFolderPathRef.current !== initialFolderPath 
    ? (initialFolderPath || '') 
    : prefix;

  // Use cache for objects and analytics - include initialFolderPath in key to handle folder switching
  const cacheKey = `objects-${bucketName}-${effectivePrefix}-${initialFolderPath || 'root'}`;
  const {
    data: cachedObjects,
    loading: cacheLoading,
    refresh: refreshCache
  } = useCache(
    cacheKey,
    async () => {
      const encodedPrefix = effectivePrefix ? encodeURIComponent(effectivePrefix) : '';
      const apiPath = encodedPrefix
        ? `/s3/list/${bucketName}/${encodedPrefix}`
        : `/s3/list/${bucketName}`;

      const response = await apiCall(apiPath);
      return {
        folders: response.folders || [],
        files: response.files || [],
        restrictedToFolder: response.restrictedToFolder || null
      };
    },
    { ttl: 2 * 60 * 1000 } // 2 minutes cache
  );

  useEffect(() => {
    if (cachedObjects) {
      setFolders(cachedObjects.folders);
      setObjects(cachedObjects.files);
      // Update restricted folder info from backend response
      if (cachedObjects.restrictedToFolder) {
        setRestrictedToFolder(cachedObjects.restrictedToFolder);
      }
      // Clear selection when navigating to different folder
      setSelectedFiles(new Set());
    }
  }, [cachedObjects]);

  // Reset state when bucket or initialFolderPath changes
  useEffect(() => {
    // Update the ref to track the current initialFolderPath
    initialFolderPathRef.current = initialFolderPath;
    
    setPrefix(initialFolderPath || '');
    setRestrictedToFolder(initialFolderPath || null);
    setSelectedFiles(new Set());
    setSearchTerm('');
  }, [bucketName, initialFolderPath]);

  useEffect(() => {
    if (refreshTrigger !== refreshTriggerRef.current && permissions.list) {
      refreshTriggerRef.current = refreshTrigger;
      refreshCache();
    }
  }, [refreshTrigger, permissions.list, refreshCache]);

  // Filter and sort objects
  const filteredAndSortedItems = useMemo(() => {
    let allItems = [...folders, ...objects];

    // Filter by search term
    if (searchTerm) {
      allItems = allItems.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort items
    if (sortConfig) {
      allItems.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    } else {
      // Default sort: folders first, then by name
      allItems.sort((a, b) => {
        if (a.isFolder && !b.isFolder) return -1;
        if (!a.isFolder && b.isFolder) return 1;
        return a.name.localeCompare(b.name);
      });
    }

    return allItems;
  }, [folders, objects, searchTerm, sortConfig]);

  const loadAnalytics = async () => {
    // Analytics are only available for admin users
    // Regular users don't need bucket analytics
    setAnalytics(null);
  };

  const downloadFile = async (key: string) => {
    if (!permissions.download) return;

    try {
      const response = await apiCall(`/s3/download/${bucketName}/${key}`);

      const link = document.createElement('a');
      link.href = response.downloadUrl;
      link.download = key.split('/').pop() || key;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('Download started');
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Failed to download file');
    }
  };

  // Multi-select handlers
  const toggleItemSelection = (key: string, isFolder: boolean) => {
    const newSelection = new Set(selectedFiles);
    if (newSelection.has(key)) {
      newSelection.delete(key);
    } else {
      newSelection.add(key);
    }
    setSelectedFiles(newSelection);
  };

  const selectAllItems = () => {
    const allKeys = [...folders.map(f => f.key), ...objects.map(obj => obj.key)];
    setSelectedFiles(new Set(allKeys));
  };

  const clearSelection = () => {
    setSelectedFiles(new Set());
  };

  // Notification context for download progress
  const { addNotification, updateNotification, removeNotification } = useNotifications();
  const [notificationId, setNotificationId] = useState<string | null>(null);

  // State for async download job
  const [downloadJobId, setDownloadJobId] = useState<string | null>(null);
  const [downloadProgress, setDownloadProgress] = useState<number>(0);
  const [downloadStatus, setDownloadStatus] = useState<string>('');
  const downloadPollRef = useRef<NodeJS.Timeout | null>(null);

  // Cancel download job
  const cancelDownload = async () => {
    if (!downloadJobId) return;
    
    try {
      await apiCall('/s3/download-job-cancel', {
        method: 'POST',
        body: JSON.stringify({ jobId: downloadJobId })
      });
      
      // Stop polling
      if (downloadPollRef.current) {
        clearInterval(downloadPollRef.current);
        downloadPollRef.current = null;
      }
      
      // Update notification
      if (notificationId) {
        updateNotification(notificationId, {
          status: 'cancelled',
          message: 'Download cancelled by user',
          canDismiss: true,
          onCancel: undefined
        });
      }
      
      // Reset state
      setDownloadJobId(null);
      setDownloadProgress(0);
      setDownloadStatus('');
      setIsDownloadingBatch(false);
      setNotificationId(null);
    } catch (error: any) {
      console.error('Error cancelling download:', error);
      if (notificationId) {
        updateNotification(notificationId, {
          message: error.message || 'Failed to cancel download'
        });
      }
    }
  };

  // Check for active download job on mount (for page refresh resume)
  useEffect(() => {
    const checkActiveJob = async () => {
      try {
        const response = await apiCall('/s3/download-job-active');
        if (response.hasActiveJob && response.jobId) {
          // Resume polling for active job
          setDownloadJobId(response.jobId);
          setIsDownloadingBatch(true);
          setDownloadProgress(response.details?.progress || 0);
          setDownloadStatus(response.status === 'processing' 
            ? `Resuming... ${response.details?.progress || 0}%` 
            : 'Resuming download...');
          
          // Create notification for resumed download
          const nId = addNotification({
            type: 'download',
            title: 'Download Resumed',
            message: `Resuming download of ${response.details?.fileCount || 'multiple'} file(s)`,
            progress: response.details?.progress || 0,
            status: response.status === 'processing' ? 'processing' : 'pending',
            canDismiss: false,
            onCancel: cancelDownload
          });
          setNotificationId(nId);
          
          // Start polling
          downloadPollRef.current = setInterval(() => {
            pollDownloadStatus(response.jobId, nId);
          }, 2000);
          
          // Poll immediately
          pollDownloadStatus(response.jobId, nId);
        }
      } catch (error) {
        console.error('Error checking active download job:', error);
      }
    };
    
    checkActiveJob();
  }, [bucketName]); // Re-check when bucket changes

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (downloadPollRef.current) {
        clearInterval(downloadPollRef.current);
      }
    };
  }, []);

  // Poll for download job status
  const pollDownloadStatus = async (jobId: string, nId?: string) => {
    const currentNotifId = nId || notificationId;
    
    try {
      const response = await apiCall(`/s3/download-job/${encodeURIComponent(jobId)}`);
      
      if (response.status === 'completed' && response.details?.downloadUrl) {
        // Stop polling
        if (downloadPollRef.current) {
          clearInterval(downloadPollRef.current);
          downloadPollRef.current = null;
        }

        setDownloadProgress(100);
        setDownloadStatus('Download ready!');
        
        // Update notification
        if (currentNotifId) {
          updateNotification(currentNotifId, {
            progress: 100,
            message: 'Download ready! Starting...',
            status: 'processing'
          });
        }

        // Download the ZIP file
        const link = document.createElement('a');
        link.href = response.details.downloadUrl;
        link.download = `download-${Date.now()}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Mark job as downloaded
        try {
          await apiCall('/s3/download-job-complete', {
            method: 'POST',
            body: JSON.stringify({ jobId })
          });
        } catch (err) {
          console.warn('Failed to mark job complete:', err);
        }

        // Update notification to completed
        if (currentNotifId) {
          updateNotification(currentNotifId, {
            status: 'completed',
            message: `Downloaded ${response.details.fileCount} file(s) as ZIP`,
            progress: 100,
            canDismiss: true,
            onCancel: undefined
          });
        }
        
        // Reset state
        setDownloadJobId(null);
        setDownloadProgress(0);
        setDownloadStatus('');
        setIsDownloadingBatch(false);
        setNotificationId(null);
        clearSelection();

      } else if (response.status === 'processing') {
        const progress = response.details?.progress || 0;
        const phase = response.details?.phase;
        setDownloadProgress(progress);
        
        // Show different message based on phase
        const statusMessage = phase === 'uploading' 
          ? 'Finalizing ZIP file...' 
          : `Creating ZIP file... ${progress}%`;
        
        setDownloadStatus(statusMessage);
        
        // Update notification
        if (currentNotifId) {
          updateNotification(currentNotifId, {
            progress,
            message: statusMessage,
            status: 'processing'
          });
        }

      } else if (response.status === 'failed' || response.status === 'cancelled') {
        // Stop polling
        if (downloadPollRef.current) {
          clearInterval(downloadPollRef.current);
          downloadPollRef.current = null;
        }

        // Update notification
        if (currentNotifId) {
          updateNotification(currentNotifId, {
            status: response.status === 'cancelled' ? 'cancelled' : 'failed',
            message: response.status === 'cancelled' 
              ? 'Download cancelled' 
              : (response.details?.error || 'Download failed'),
            canDismiss: true,
            onCancel: undefined
          });
        }
        
        setDownloadJobId(null);
        setDownloadProgress(0);
        setDownloadStatus('');
        setIsDownloadingBatch(false);
        setNotificationId(null);

      } else if (response.status === 'pending') {
        setDownloadStatus('Preparing download...');
        if (currentNotifId) {
          updateNotification(currentNotifId, {
            message: 'Preparing download...',
            status: 'pending'
          });
        }
      }
    } catch (error: any) {
      console.error('Error polling download status:', error);
      // Don't stop polling on transient errors, but log them
    }
  };

  // Download limits (must match backend DOWNLOAD_JOB_LIMITS)
  const DOWNLOAD_LIMITS = {
    maxFiles: 100,
    maxTotalSize: 2 * 1024 * 1024 * 1024 // 2GB - ZipProcessor has 15min timeout
  };

  // Format bytes to human readable
  const formatBytesForLimit = (bytes: number): string => {
    if (bytes >= 1024 * 1024 * 1024) {
      return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const downloadSelectedFiles = async () => {
    if (!permissions.download || selectedFiles.size === 0) return;

    // Check if a download is already in progress
    if (isDownloadingBatch || downloadJobId) {
      toast.error('A download is already in progress. Please wait for it to complete.');
      return;
    }

    const selectedArray = Array.from(selectedFiles);
    
    // Prepare items for ZIP download
    const items = selectedArray.map(key => {
      const isFolder = folders.some(f => f.key === key);
      return { key, isFolder };
    });

    // Frontend pre-check: estimate file count and size
    // Note: For folders, we can't know exact count/size without backend call,
    // but we can check selected files and warn about folder selections
    const selectedFolders = items.filter(i => i.isFolder);
    const selectedFilesOnly = items.filter(i => !i.isFolder);
    
    // Calculate size of selected files (not folders)
    let estimatedSize = 0;
    let estimatedFileCount = selectedFilesOnly.length;
    
    for (const item of selectedFilesOnly) {
      const fileObj = objects.find(o => o.key === item.key);
      if (fileObj) {
        estimatedSize += fileObj.size;
      }
    }

    // If only files selected (no folders), we can do accurate pre-check
    if (selectedFolders.length === 0) {
      if (estimatedFileCount > DOWNLOAD_LIMITS.maxFiles) {
        toast.error(`Selection too large. Maximum ${DOWNLOAD_LIMITS.maxFiles} files allowed per download. You selected ${estimatedFileCount} files.`);
        return;
      }
      
      if (estimatedSize > DOWNLOAD_LIMITS.maxTotalSize) {
        toast.error(`Selection too large. Maximum ${formatBytesForLimit(DOWNLOAD_LIMITS.maxTotalSize)} allowed per download. You selected ${formatBytesForLimit(estimatedSize)}.`);
        return;
      }
    } else {
      // If folders are selected, show warning if file count is already high
      if (estimatedFileCount > DOWNLOAD_LIMITS.maxFiles * 0.8) {
        toast.error(`You've selected ${estimatedFileCount} files plus ${selectedFolders.length} folder(s). This may exceed the ${DOWNLOAD_LIMITS.maxFiles} file limit. Try selecting fewer items.`);
        return;
      }
      
      // Warn if size is already close to limit
      if (estimatedSize > DOWNLOAD_LIMITS.maxTotalSize * 0.8) {
        toast.error(`You've selected ${formatBytesForLimit(estimatedSize)} plus ${selectedFolders.length} folder(s). This may exceed the ${formatBytesForLimit(DOWNLOAD_LIMITS.maxTotalSize)} limit. Try selecting fewer items.`);
        return;
      }
    }

    setIsDownloadingBatch(true);
    
    // Create notification for download
    const nId = addNotification({
      type: 'download',
      title: 'Download Started',
      message: `Preparing ${items.length} item(s) for download...`,
      progress: 0,
      status: 'pending',
      canDismiss: false,
      onCancel: cancelDownload
    });
    setNotificationId(nId);

    try {
      // Call backend to start async ZIP download job
      const response = await apiCall(`/s3/download-zip/${bucketName}`, {
        method: 'POST',
        body: JSON.stringify({
          items,
          basePath: prefix
        })
      });

      // Check if there's already an active job (409 conflict)
      if (response.activeJobId) {
        updateNotification(nId, {
          status: 'failed',
          message: 'A download is already in progress. Please wait for it to complete.',
          canDismiss: true,
          onCancel: undefined
        });
        setIsDownloadingBatch(false);
        setNotificationId(null);
        return;
      }

      // Job started successfully
      if (response.jobId) {
        setDownloadJobId(response.jobId);
        setDownloadProgress(0);
        setDownloadStatus('Preparing download...');
        
        updateNotification(nId, {
          message: `Preparing ${response.fileCount} file(s) for download...`,
          status: 'pending'
        });

        // Start polling for job status
        downloadPollRef.current = setInterval(() => {
          pollDownloadStatus(response.jobId, nId);
        }, 2000); // Poll every 2 seconds

        // Also poll immediately
        pollDownloadStatus(response.jobId, nId);
      } else {
        throw new Error('No job ID received');
      }
    } catch (error: any) {
      console.error('Error starting ZIP download:', error);
      const errorMessage = error.message || 'Failed to start download';
      
      // Show toast for important errors
      if (errorMessage.includes('Monthly data transfer limit') || 
          errorMessage.includes('Selection too large') ||
          errorMessage.includes('No download permission')) {
        toast.error(errorMessage);
      }
      
      updateNotification(nId, {
        status: 'failed',
        message: errorMessage,
        canDismiss: true,
        onCancel: undefined
      });
      setIsDownloadingBatch(false);
      setDownloadJobId(null);
      setDownloadProgress(0);
      setDownloadStatus('');
      setNotificationId(null);
    }
  };

  const showDeleteConfirmation = (key: string, fileName: string) => {
    if (!permissions.delete) return;
    setDeleteConfirmation({ key, fileName });
  };

  const confirmDelete = async () => {
    if (!deleteConfirmation) return;

    try {
      await apiCall(`/s3/delete/${bucketName}/${encodeURIComponent(deleteConfirmation.key)}`, {
        method: 'DELETE'
      });

      toast.success(`File "${deleteConfirmation.fileName}" deleted successfully`);
      setDeleteConfirmation(null);
      refreshCache();
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error('Failed to delete file');
      setDeleteConfirmation(null);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmation(null);
  };

  const previewFile = async (object: S3Object) => {
    try {
      let response;
      if (permissions.download) {
        // Add preview=true to indicate this is an intentional user preview
        response = await apiCall(`/s3/thumbnail/${bucketName}/${object.key}?preview=true`);
        setPreviewModal({
          url: response.thumbnailUrl,
          type: object.isVideo ? 'video' : 'image',
          name: object.name
        });
      } else if (permissions.download) {
        response = await apiCall(`/s3/download/${bucketName}/${object.key}`);
        setPreviewModal({
          url: response.downloadUrl,
          type: object.isVideo ? 'video' : 'image',
          name: object.name
        });
      }
    } catch (error) {
      console.error('Error getting preview URL:', error);
      toast.error('Failed to load preview');
    }
  };

  const navigateToFolder = (folderKey: string) => {
    setPrefix(folderKey);
    onPrefixChange?.(folderKey);
  };

  const navigateUp = () => {
    const parts = prefix.split('/').filter(p => p);
    parts.pop();
    const newPrefix = parts.length > 0 ? parts.join('/') + '/' : '';
    
    // Prevent navigating above the restricted folder
    if (restrictedToFolder && !newPrefix.startsWith(restrictedToFolder) && newPrefix !== restrictedToFolder) {
      // Can't go above restricted folder
      return;
    }
    
    setPrefix(newPrefix);
    onPrefixChange?.(newPrefix);
  };

  const navigateToPath = (path: string) => {
    // Prevent navigating above the restricted folder
    if (restrictedToFolder && !path.startsWith(restrictedToFolder) && path !== '' && path !== restrictedToFolder) {
      // Can't navigate outside restricted folder
      return;
    }
    
    // If navigating to root but restricted, go to restricted folder instead
    if (restrictedToFolder && path === '') {
      setPrefix(restrictedToFolder);
      onPrefixChange?.(restrictedToFolder);
      return;
    }
    
    setPrefix(path);
    onPrefixChange?.(path);
  };

  const getBreadcrumbs = (): BreadcrumbItem[] => {
    const breadcrumbs: BreadcrumbItem[] = [];
    
    // If restricted to a folder, start breadcrumbs from that folder
    if (restrictedToFolder) {
      // Add bucket name but it won't be clickable (handled in render)
      breadcrumbs.push({ name: bucketName, path: '' });
      
      // Add the restricted folder parts
      const restrictedParts = restrictedToFolder.split('/').filter(p => p);
      let currentPath = '';
      restrictedParts.forEach((part, index) => {
        currentPath += part + '/';
        breadcrumbs.push({
          name: part,
          path: currentPath
        });
      });
      
      // Add any additional path beyond the restricted folder
      if (prefix && prefix !== restrictedToFolder && prefix.startsWith(restrictedToFolder)) {
        const additionalPath = prefix.substring(restrictedToFolder.length);
        const additionalParts = additionalPath.split('/').filter(p => p);
        additionalParts.forEach((part) => {
          currentPath += part + '/';
          breadcrumbs.push({
            name: part,
            path: currentPath
          });
        });
      }
    } else {
      // Normal breadcrumbs - full bucket access
      breadcrumbs.push({ name: bucketName, path: '' });

      if (prefix) {
        const parts = prefix.split('/').filter(p => p);
        let currentPath = '';

        parts.forEach((part, index) => {
          currentPath += part + '/';
          breadcrumbs.push({
            name: part,
            path: currentPath
          });
        });
      }
    }

    return breadcrumbs;
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) {
      toast.error('Please enter a folder name');
      return;
    }

    try {
      const folderPath = prefix + newFolderName.trim();

      await apiCall(`/s3/create-folder/${bucketName}`, {
        method: 'POST',
        body: JSON.stringify({
          folderPath: folderPath
        })
      });

      toast.success(`Folder "${newFolderName}" created successfully`);
      setNewFolderName('');
      setShowCreateFolder(false);
      refreshCache();
    } catch (error) {
      console.error('Error creating folder:', error);
      toast.error('Failed to create folder');
    }
  };



  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (!permissions.list) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No List Permission</h3>
          <p className="text-gray-500">You don't have permission to browse files in this bucket.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Analytics Panel */}
      {analytics && (
        <div className="bg-white shadow-sm rounded-xl p-6 mb-6 border border-gray-200">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Storage Analytics</h3>
            <button
              onClick={loadAnalytics}
              disabled={analyticsLoading}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
            >
              <svg className={`w-4 h-4 mr-2 ${analyticsLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                    <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-blue-900">Total Files</p>
                  <p className="text-2xl font-bold text-blue-700">{analytics.fileCount.toLocaleString()}</p>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl border border-green-200">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center">
                    <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-green-900">Storage Used</p>
                  <p className="text-2xl font-bold text-green-700">{formatFileSize(analytics.totalSize)}</p>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl border border-purple-200">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center">
                    <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-purple-900">Last Activity</p>
                  <p className="text-sm font-semibold text-purple-700">
                    {analytics.lastModified ? new Date(analytics.lastModified).toLocaleDateString() : 'No activity'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}



      {/* File Browser - Clean and Separate */}
      <div className="bg-white shadow-sm rounded-xl overflow-hidden border-2 border-gray-200 hover:border-gray-300 transition-all duration-200">
        {/* Enhanced Header with Breadcrumbs */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
          {/* Breadcrumb Navigation */}
          <div className="flex items-center space-x-2 mb-4">
            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
            </svg>
            <nav className="flex" aria-label="Breadcrumb">
              <ol className="flex items-center space-x-2">
                {getBreadcrumbs().map((breadcrumb, index) => {
                  const isLast = index === getBreadcrumbs().length - 1;
                  // Check if this breadcrumb is within or at the restricted folder boundary
                  const isWithinRestriction = !restrictedToFolder || 
                    breadcrumb.path === '' || 
                    breadcrumb.path.startsWith(restrictedToFolder) ||
                    restrictedToFolder.startsWith(breadcrumb.path);
                  // Disable clicking on paths above the restricted folder
                  const isClickable = !isLast && (!restrictedToFolder || 
                    breadcrumb.path === restrictedToFolder || 
                    (breadcrumb.path.startsWith(restrictedToFolder) && breadcrumb.path !== ''));
                  
                  return (
                    <li key={breadcrumb.path} className="flex items-center">
                      {index > 0 && (
                        <svg className="flex-shrink-0 h-4 w-4 text-gray-400 mx-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                      <button
                        onClick={() => isClickable ? navigateToPath(breadcrumb.path) : null}
                        disabled={!isClickable}
                        className={`text-sm font-medium transition-colors ${
                          isLast
                            ? 'text-gray-900 cursor-default'
                            : isClickable
                              ? 'text-blue-600 hover:text-blue-800'
                              : 'text-gray-400 cursor-not-allowed'
                        }`}
                        title={!isClickable && restrictedToFolder ? 'Access restricted to folder' : undefined}
                      >
                        {breadcrumb.name}
                      </button>
                      {/* Show lock icon for restricted folder root */}
                      {restrictedToFolder && breadcrumb.path === restrictedToFolder && (
                        <span className="ml-1 text-blue-500" title="Your access is restricted to this folder">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                          </svg>
                        </span>
                      )}
                    </li>
                  );
                })}
              </ol>
            </nav>
            {/* Folder restriction indicator */}
            {restrictedToFolder && (
              <span className="ml-3 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                Folder Access
              </span>
            )}
          </div>

          {/* Search and Filter Bar */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4 flex-1">
              <div className="relative flex-1 max-w-md">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Search files and folders..."
                />
              </div>

              <div className="flex items-center space-x-2">
                <select
                  value={sortConfig ? `${sortConfig.key}-${sortConfig.direction}` : 'default'}
                  onChange={(e) => {
                    if (e.target.value === 'default') {
                      setSortConfig(null);
                    } else {
                      const [key, direction] = e.target.value.split('-');
                      setSortConfig({
                        key: key as keyof S3Object,
                        direction: direction as 'asc' | 'desc'
                      });
                    }
                  }}
                  className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="default">Default Sort</option>
                  <option value="name-asc">Name A-Z</option>
                  <option value="name-desc">Name Z-A</option>
                  <option value="size-asc">Size (Small to Large)</option>
                  <option value="size-desc">Size (Large to Small)</option>
                  <option value="lastModified-desc">Newest First</option>
                  <option value="lastModified-asc">Oldest First</option>
                </select>
              </div>
            </div>
          </div>

          {/* Action Bar */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-600">
                {searchTerm ? (
                  <>
                    {filteredAndSortedItems.length} of {folders.length + objects.length} items
                    {searchTerm && (
                      <span className="ml-2 text-blue-600">
                        matching "{searchTerm}"
                      </span>
                    )}
                  </>
                ) : (
                  `${folders.length + objects.length} items`
                )}
                {selectedFiles.size > 0 && (
                  <span className="ml-2 text-blue-600 font-medium">
                    • {selectedFiles.size} item{selectedFiles.size > 1 ? 's' : ''} selected
                  </span>
                )}
              </div>
              {(folders.length > 0 || objects.length > 0) && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600'
                      }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600'
                      }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                  </button>
                </div>
              )}
            </div>

            <div className="flex items-center space-x-3">
              {permissions.upload && (
                <button
                  onClick={() => setShowCreateFolder(true)}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  New Folder
                </button>
              )}
              <button
                onClick={refreshCache}
                disabled={cacheLoading}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
              >
                <svg className={`w-4 h-4 mr-2 ${cacheLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
            </div>
          </div>

          {/* Multi-select Toolbar */}
          {(selectedFiles.size > 0 || isDownloadingBatch) && permissions.download && (
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <span className="text-sm font-medium text-blue-900">
                    {isDownloadingBatch 
                      ? 'Download in progress - check notifications for status' 
                      : `${selectedFiles.size} item${selectedFiles.size > 1 ? 's' : ''} selected`}
                  </span>
                  {!isDownloadingBatch && (objects.length > 0 || folders.length > 0) && (
                    <button
                      onClick={selectedFiles.size === (objects.length + folders.length) ? clearSelection : selectAllItems}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                    >
                      {selectedFiles.size === (objects.length + folders.length) ? 'Deselect All' : 'Select All'}
                    </button>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  {isDownloadingBatch ? (
                    <button
                      onClick={cancelDownload}
                      className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      Cancel Download
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={downloadSelectedFiles}
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        Download Selected
                      </button>
                      <button
                        onClick={clearSelection}
                        className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Clear
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Content Area - Clean File Browser */}
        <div className="p-6 min-h-96">
          {cacheLoading && !cachedObjects ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
              <p className="text-gray-600">Loading files...</p>
            </div>
          ) : filteredAndSortedItems.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {searchTerm ? 'No matching files found' : (permissions.upload ? 'No files yet' : 'Folder is empty')}
              </h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">
                {searchTerm ? (
                  <>
                    No files or folders match "{searchTerm}". Try adjusting your search terms.
                    <button
                      onClick={() => setSearchTerm('')}
                      className="block mx-auto mt-2 text-blue-600 hover:text-blue-800"
                    >
                      Clear search
                    </button>
                  </>
                ) : (
                  permissions.upload
                    ? 'Drag and drop files here, click to browse, or create a new folder to get started.'
                    : 'This folder contains no files or you may not have permission to view them.'
                )}
              </p>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
              {filteredAndSortedItems.map((item) => (
                item.isFolder ? (
                  <div
                    key={item.key}
                    className={`group relative bg-white border-2 rounded-xl p-4 hover:border-blue-300 hover:shadow-md transition-all duration-200 ${selectedFiles.has(item.key) ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
                  >
                    {/* Selection Checkbox */}
                    {permissions.download && (
                      <div className="absolute top-2 left-2 z-10">
                        <input
                          type="checkbox"
                          checked={selectedFiles.has(item.key)}
                          onChange={(e) => {
                            e.stopPropagation();
                            toggleItemSelection(item.key, true);
                          }}
                          className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                        />
                      </div>
                    )}
                    <div 
                      className="flex flex-col items-center text-center cursor-pointer"
                      onClick={() => navigateToFolder(item.key)}
                    >
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mb-3 group-hover:from-blue-600 group-hover:to-blue-700 transition-colors">
                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                        </svg>
                      </div>
                      <p className="text-sm font-medium text-gray-900 truncate w-full" title={item.name}>
                        {item.name.replace('/', '')}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">Folder</p>
                    </div>
                  </div>
                ) : (
                  <FileItem
                    key={item.key}
                    object={item}
                    onDownload={() => downloadFile(item.key)}
                    onPreview={() => previewFile(item)}
                    onDelete={() => showDeleteConfirmation(item.key, item.name)}
                    canDownload={permissions.download}
                    canDelete={permissions.delete}
                    bucketName={bucketName}
                    formatFileSize={formatFileSize}
                    viewMode={viewMode}
                    isSelected={selectedFiles.has(item.key)}
                    onToggleSelect={() => toggleItemSelection(item.key, false)}
                  />
                )
              ))}
            </div>
          ) : (
            <VirtualizedList
              items={filteredAndSortedItems}
              itemHeight={80}
              containerHeight={600}
              className="border border-gray-200 rounded-lg"
              renderItem={(item, index) => (
                item.isFolder ? (
                  <div
                    key={item.key}
                    className={`flex items-center justify-between p-4 border-b border-gray-100 hover:bg-gray-50 transition-all duration-200 group ${selectedFiles.has(item.key) ? 'bg-blue-50' : ''}`}
                    style={{ height: 80 }}
                  >
                    <div className="flex items-center space-x-4">
                      {/* Selection Checkbox */}
                      {permissions.download && (
                        <input
                          type="checkbox"
                          checked={selectedFiles.has(item.key)}
                          onChange={(e) => {
                            e.stopPropagation();
                            toggleItemSelection(item.key, true);
                          }}
                          className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer flex-shrink-0"
                        />
                      )}
                      <div 
                        className="flex items-center space-x-4 cursor-pointer flex-1"
                        onClick={() => navigateToFolder(item.key)}
                      >
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center group-hover:from-blue-600 group-hover:to-blue-700 transition-colors">
                          <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{item.name.replace('/', '')}</p>
                          <p className="text-xs text-gray-500">Folder</p>
                        </div>
                      </div>
                      <svg className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                ) : (
                  <div style={{ height: 80 }}>
                    <FileItem
                      key={item.key}
                      object={item}
                      onDownload={() => downloadFile(item.key)}
                      onPreview={() => previewFile(item)}
                      onDelete={() => showDeleteConfirmation(item.key, item.name)}
                      canDownload={permissions.download}
                      canDelete={permissions.delete}
                      bucketName={bucketName}
                      formatFileSize={formatFileSize}
                      viewMode="list"
                      isSelected={selectedFiles.has(item.key)}
                      onToggleSelect={() => toggleItemSelection(item.key, false)}
                    />
                  </div>
                )
              )}
            />
          )}
        </div>
      </div>

      {/* Create Folder Modal */}
      {showCreateFolder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Create New Folder</h3>
              <button
                onClick={() => {
                  setShowCreateFolder(false);
                  setNewFolderName('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Folder Name
                </label>
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="Enter folder name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  onKeyPress={(e) => e.key === 'Enter' && createFolder()}
                  autoFocus
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowCreateFolder(false);
                    setNewFolderName('');
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={createFolder}
                  disabled={!newFolderName.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Create Folder
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="relative max-w-4xl max-h-full bg-white rounded-xl overflow-hidden shadow-2xl">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{previewModal.name}</h3>
              <button
                onClick={() => setPreviewModal(null)}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              {previewModal.type === 'video' ? (
                <video
                  src={previewModal.url}
                  controls
                  className="max-w-full max-h-96 mx-auto rounded-lg"
                  autoPlay
                >
                  Your browser does not support the video tag.
                </video>
              ) : (
                <img
                  src={previewModal.url}
                  alt={previewModal.name}
                  className="max-w-full max-h-96 mx-auto object-contain rounded-lg"
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
            {/* Header with Warning Icon */}
            <div className="flex items-center justify-center pt-6 pb-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 pb-6">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Delete File</h3>
                <p className="text-gray-600 mb-4">
                  Are you sure you want to delete this file?
                </p>

                {/* File Info */}
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-center space-x-3">
                    <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="font-medium text-gray-900 break-all">
                      {deleteConfirmation.fileName}
                    </span>
                  </div>
                </div>

                {/* Warning Messages */}
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start space-x-3">
                    <svg className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="text-left">
                      <p className="text-sm font-medium text-red-800 mb-1">
                        This action cannot be undone
                      </p>
                      <p className="text-sm text-red-700">
                        The file will be permanently deleted from the storage bucket and cannot be recovered.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3">
                <button
                  onClick={cancelDelete}
                  className="flex-1 px-4 py-3 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-colors focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                  Delete File
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

interface FileItemProps {
  object: S3Object;
  onDownload: () => void;
  onPreview: () => void;
  onDelete: () => void;
  canDownload: boolean;
  canDelete: boolean;
  bucketName: string;
  formatFileSize: (bytes: number) => string;
  viewMode: 'list' | 'grid';
  isSelected?: boolean;
  onToggleSelect?: () => void;
}

const FileItem: React.FC<FileItemProps> = ({
  object,
  onDownload,
  onPreview,
  onDelete,
  canDownload,
  canDelete,
  bucketName,
  formatFileSize,
  viewMode,
  isSelected = false,
  onToggleSelect
}) => {
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);

  useEffect(() => {
    if (canDownload && (object.isImage || object.isVideo)) {
      getThumbnail();
    }
  }, [object.key, object.isImage, object.isVideo, canDownload]);

  const getThumbnail = async () => {
    try {
      // Add preview=false to indicate this is for thumbnail display, not user preview
      const response = await apiCall(`/s3/thumbnail/${bucketName}/${object.key}?preview=false`);
      setThumbnailUrl(response.thumbnailUrl);
    } catch (error) {
      console.error('Error getting thumbnail:', error);
    }
  };

  const canPreview = canDownload && (object.isImage || object.isVideo);

  const getFileIcon = () => {
    if (object.isImage) {
      return (
        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      );
    } else if (object.isVideo) {
      return (
        <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      );
    } else {
      return (
        <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      );
    }
  };

  if (viewMode === 'grid') {
    return (
      <div className={`group bg-white border-2 rounded-xl p-4 hover:border-blue-300 hover:shadow-md transition-all duration-200 ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
        <div className="flex flex-col items-center text-center">
          {/* Selection Checkbox */}
          {canDownload && onToggleSelect && (
            <div className="absolute top-2 left-2">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={(e) => {
                  e.stopPropagation();
                  onToggleSelect();
                }}
                className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
              />
            </div>
          )}
          {/* Thumbnail or Icon */}
          <div className="w-16 h-16 mb-3 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
            {thumbnailUrl && canDownload ? (
              <div
                className="relative cursor-pointer w-full h-full"
                onClick={canPreview ? onPreview : undefined}
              >
                {object.isVideo ? (
                  <div className="relative w-full h-full">
                    <video
                      src={thumbnailUrl}
                      className="w-full h-full object-cover"
                      muted
                      preload="metadata"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                ) : (
                  <img
                    src={thumbnailUrl}
                    alt={object.name}
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
            ) : (
              <div
                className={`w-full h-full flex items-center justify-center ${canPreview ? 'cursor-pointer' : ''}`}
                onClick={canPreview ? onPreview : undefined}
              >
                {getFileIcon()}
              </div>
            )}
          </div>

          {/* File Info */}
          <p className="text-sm font-medium text-gray-900 truncate w-full mb-1" title={object.name}>
            {object.name}
          </p>
          <p className="text-xs text-gray-500 mb-3">
            {formatFileSize(object.size)}
          </p>

          {/* Actions */}
          <div className="flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
            {canPreview && (
              <button
                onClick={onPreview}
                className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
              >
                Preview
              </button>
            )}
            {canDownload && (
              <button
                onClick={onDownload}
                className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
              >
                Download
              </button>
            )}
            {canDelete && (
              <button
                onClick={onDelete}
                className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
              >
                Delete
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className={`flex items-center justify-between p-4 border rounded-xl hover:bg-gray-50 hover:border-blue-300 transition-all duration-200 group ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
      <div className="flex items-center space-x-4">
        {/* Selection Checkbox */}
        {canDownload && onToggleSelect && (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => {
              e.stopPropagation();
              onToggleSelect();
            }}
            className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer flex-shrink-0"
          />
        )}
        {/* Thumbnail or Icon */}
        <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center flex-shrink-0">
          {thumbnailUrl && canDownload ? (
            <div
              className="relative cursor-pointer w-full h-full"
              onClick={canPreview ? onPreview : undefined}
            >
              {object.isVideo ? (
                <div className="relative w-full h-full">
                  <video
                    src={thumbnailUrl}
                    className="w-full h-full object-cover"
                    muted
                    preload="metadata"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              ) : (
                <img
                  src={thumbnailUrl}
                  alt={object.name}
                  className="w-full h-full object-cover"
                />
              )}
            </div>
          ) : (
            <div
              className={`w-full h-full flex items-center justify-center ${canPreview ? 'cursor-pointer' : ''}`}
              onClick={canPreview ? onPreview : undefined}
            >
              {getFileIcon()}
            </div>
          )}
        </div>

        {/* File Info */}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-900 truncate">{object.name}</p>
          <p className="text-xs text-gray-500">
            {formatFileSize(object.size)} • {new Date(object.lastModified).toLocaleDateString()}
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
        {canPreview && (
          <button
            onClick={onPreview}
            className="text-blue-600 hover:text-blue-800 text-sm px-3 py-1 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
          >
            Preview
          </button>
        )}
        {canDownload && (
          <button
            onClick={onDownload}
            className="text-green-600 hover:text-green-800 text-sm px-3 py-1 border border-green-600 rounded-lg hover:bg-green-50 transition-colors"
          >
            Download
          </button>
        )}
        {canDelete && (
          <button
            onClick={onDelete}
            className="text-red-600 hover:text-red-800 text-sm px-3 py-1 border border-red-600 rounded-lg hover:bg-red-50 transition-colors"
          >
            Delete
          </button>
        )}
      </div>
    </div>
  );
};

export default FileBrowser;